import { Component, OnInit, OnDestroy, ViewEncapsulation, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { WebhooksService } from '../../services/requests/webhooks.service';
import { ModalClickOutsideService } from '../../services/modal-click-outside.service';

declare function showNotif(message: string, type?: string): void;

@Component({
  selector: 'app-webhooks',
  templateUrl: './webhooks.component.html',
  styleUrls: ['./webhooks.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class WebhooksComponent implements OnInit, OnDestroy, AfterViewInit {
  webhooks: any[] = [];
  filtered: any[] = [];
  isLoading = false;
  showAddModal = false;
  showEditModal = false;
  selectedWebhook: any = null;

  // Formulaire
  formNom = '';
  formWebhookUrlReceipt = '';
  formResponseJsonData = '{}';
  formPhoneNumberFormat = '0';
  formToken = '';

  // Recherche
  searchTerm: string = '';

  // Tri
  sortField: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  @ViewChild('addModalContent', { static: false }) addModalContent!: ElementRef;
  @ViewChild('editModalContent', { static: false }) editModalContent!: ElementRef;

  constructor(
    private router: Router,
    private webhooksService: WebhooksService,
    private modalClickOutsideService: ModalClickOutsideService
  ) {}

  ngOnInit(): void {
    if (localStorage.getItem('isAdmin') !== 'true') {
      this.router.navigate(['/suivi']);
    }

    // Récupérer le tri sauvegardé depuis localStorage
    const savedSort = localStorage.getItem('localStorage_SP-Webhooks');
    if (savedSort) {
      try {
        const sortData = JSON.parse(savedSort);
        this.sortField = sortData.field || '';
        this.sortDirection = sortData.direction || 'asc';
      } catch (e) {
        console.error('Erreur lors de la récupération du tri webhooks :', e);
      }
    }

    this.getWebhooks();
  }

  ngAfterViewInit(): void {
    this.updateModalRegistration();
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';
    if (this.addModalContent) {
      this.modalClickOutsideService.unregisterModal(this.addModalContent);
    }
    if (this.editModalContent) {
      this.modalClickOutsideService.unregisterModal(this.editModalContent);
    }
  }

  private updateModalRegistration(): void {
    if (this.addModalContent) {
      if (this.showAddModal) {
        this.modalClickOutsideService.registerModal(this.addModalContent, () => this.closeModals());
      } else {
        this.modalClickOutsideService.unregisterModal(this.addModalContent);
      }
    }
    if (this.editModalContent) {
      if (this.showEditModal) {
        this.modalClickOutsideService.registerModal(this.editModalContent, () => this.closeModals());
      } else {
        this.modalClickOutsideService.unregisterModal(this.editModalContent);
      }
    }
  }

  private updateScroll(): void {
    document.body.style.overflow = (this.showAddModal || this.showEditModal) ? 'hidden' : '';
  }

  toggleSort(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    
    // Sauvegarder le tri dans localStorage
    try {
      localStorage.setItem('localStorage_SP-Webhooks', JSON.stringify({
        field: this.sortField,
        direction: this.sortDirection
      }));
    } catch (e) {
      console.error('Erreur lors de la sauvegarde du tri webhooks :', e);
    }
    
    this.applyFilter();
  }

  applyFilter(): void {
    let result = [...this.webhooks];
    
    // Filtre par recherche
    const term = this.searchTerm.toLowerCase().trim();
    if (term) {
      result = result.filter((w) => {
        const nameMatch = w.nom?.toLowerCase().includes(term);
        const urlMatch = w.webhookUrlReceipt?.toLowerCase().includes(term);
        return nameMatch || urlMatch;
      });
    }

    // Tri par colonne
    if (this.sortField) {
      result.sort((a, b) => {
        let valueA: any;
        let valueB: any;
        
        switch (this.sortField) {
          case 'nom':
            valueA = a.nom?.toLowerCase() || '';
            valueB = b.nom?.toLowerCase() || '';
            return this.sortDirection === 'asc' 
              ? valueA.localeCompare(valueB) 
              : valueB.localeCompare(valueA);
          
          case 'webhookUrlReceipt':
            valueA = a.webhookUrlReceipt?.toLowerCase() || '';
            valueB = b.webhookUrlReceipt?.toLowerCase() || '';
            return this.sortDirection === 'asc' 
              ? valueA.localeCompare(valueB) 
              : valueB.localeCompare(valueA);
          
          default:
            return 0;
        }
      });
    }

    this.filtered = result;
  }

  copyWebhook(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      showNotif('📋 URL webhook copiée', 'success');
    }).catch(() => {
      console.error('❌ Erreur lors de la copie');
      showNotif('❌ Erreur lors de la copie de l\'URL webhook', 'error');
    });
  }

  formatJsonData(responseJsonData: any): string {
    if (!responseJsonData) {
      return '{}';
    }
    if (typeof responseJsonData === 'string') {
      try {
        const parsed = JSON.parse(responseJsonData);
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        return responseJsonData;
      }
    }
    return JSON.stringify(responseJsonData, null, 2);
  }

  getWebhooks(): void {
    this.isLoading = true;

    this.webhooksService.getAll().subscribe({
      next: (response: any) => {
        this.isLoading = false;

        if (response.status) {
          this.webhooks = response.data?.webhooks || [];
          this.applyFilter();
        } else {
          showNotif(response.message || "❌ Erreur lors de la récupération", 'error');
        }
      },
      error: (error: any) => {
        this.isLoading = false;
        console.error("Erreur getWebhooks() :", error);
        showNotif(error?.error?.message || "Erreur chargement webhooks", 'error');
      }
    });
  }

  openAddModal(): void {
    this.formNom = '';
    this.formWebhookUrlReceipt = '';
    this.formResponseJsonData = '{}';
    this.formPhoneNumberFormat = '0';
    this.formToken = '';
    this.showAddModal = true;
    this.updateScroll();
    setTimeout(() => {
      this.updateModalRegistration();
    }, 0);
  }

  openEditModal(webhook: any): void {
    this.selectedWebhook = webhook;
    this.formNom = webhook.nom || '';
    this.formWebhookUrlReceipt = webhook.webhookUrlReceipt || '';
    this.formResponseJsonData = webhook.responseJsonData ? JSON.stringify(webhook.responseJsonData, null, 2) : '{}';
    this.formPhoneNumberFormat = webhook.phoneNumberFormat || '0';
    this.formToken = webhook.token || '';
    this.showEditModal = true;
    this.updateScroll();
    setTimeout(() => {
      this.updateModalRegistration();
    }, 0);
  }

  closeModals(): void {
    if (!this.isLoading) {
      this.showAddModal = false;
      this.showEditModal = false;
      this.selectedWebhook = null;
      this.formNom = '';
      this.formWebhookUrlReceipt = '';
      this.formResponseJsonData = '{}';
      this.formPhoneNumberFormat = '0';
      this.formToken = '';
      this.updateScroll();
      this.updateModalRegistration();
    }
  }

  addWebhook(): void {
    if (!this.formNom || !this.formWebhookUrlReceipt) {
      showNotif('Veuillez remplir tous les champs', 'error');
      return;
    }

    let responseJsonData = {};
    try {
      responseJsonData = this.formResponseJsonData ? JSON.parse(this.formResponseJsonData) : {};
    } catch (e) {
      showNotif('❌ Format JSON invalide', 'error');
      return;
    }

    this.isLoading = true;
    this.webhooksService.add({
      nom: this.formNom,
      webhookUrlReceipt: this.formWebhookUrlReceipt,
      responseJsonData: responseJsonData,
      phoneNumberFormat: this.formPhoneNumberFormat,
      token: this.formToken
    }).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        if (response.status) {
          showNotif('✅ Webhook ajouté avec succès', 'success');
          this.closeModals();
          this.getWebhooks();
        } else {
          showNotif(response.message || "Erreur lors de l'ajout", 'error');
        }
      },
      error: (error: any) => {
        this.isLoading = false;
        console.error("❌ Erreur addWebhook :", error);
        showNotif("Erreur serveur", 'error');
      }
    });
  }

  updateWebhook(): void {
    if (!this.selectedWebhook || !this.formNom || !this.formWebhookUrlReceipt) {
      showNotif('Veuillez remplir tous les champs', 'error');
      return;
    }

    let responseJsonData = {};
    try {
      responseJsonData = this.formResponseJsonData ? JSON.parse(this.formResponseJsonData) : {};
    } catch (e) {
      showNotif('❌ Format JSON invalide', 'error');
      return;
    }

    this.isLoading = true;
    this.webhooksService.update({
      id: this.selectedWebhook._id,
      nom: this.formNom,
      webhookUrlReceipt: this.formWebhookUrlReceipt,
      responseJsonData: responseJsonData,
      phoneNumberFormat: this.formPhoneNumberFormat,
      token: this.formToken
    }).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        if (response.status) {
          showNotif('✅ Webhook mis à jour avec succès', 'success');
          this.closeModals();
          this.getWebhooks();
        } else {
          showNotif(response.message || "Erreur lors de la mise à jour", 'error');
        }
      },
      error: (error: any) => {
        this.isLoading = false;
        console.error("❌ Erreur updateWebhook :", error);
        showNotif("Erreur serveur", 'error');
      }
    });
  }

  deleteWebhook(webhook: any): void {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer le webhook "${webhook.nom}" ?`)) {
      return;
    }

    this.isLoading = true;
    this.webhooksService.delete({
      id: webhook._id
    }).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        if (response.status) {
          showNotif('✅ Webhook supprimé avec succès', 'success');
          this.getWebhooks();
        } else {
          showNotif(response.message || "Erreur lors de la suppression", 'error');
        }
      },
      error: (error: any) => {
        this.isLoading = false;
        console.error("❌ Erreur deleteWebhook :", error);
        showNotif("Erreur serveur", 'error');
      }
    });
  }
}

